import React, { useState } from 'react';
import { useBranding } from '../Context/BrandingContext';
import { getRmas } from '../api/rmas';
import { CONNECT_ROLES } from '../utils/roles';
import { isInsufficientPermissionError } from '../utils/permissionErrors';
import AppHeader from './AppHeader';
import PermissionGate from './PermissionGate';
import { useMarketplaceList } from './marketplace/useMarketplaceList';
import { listStyles as s, statusStyle, formatDate } from './marketplace/listStyles';

const SORT_OPTIONS = [
  { value: 'date_desc', label: 'Newest first' },
  { value: 'date_asc', label: 'Oldest first' },
  { value: 'name_asc', label: 'RMA # (A-Z)' },
  { value: 'name_desc', label: 'RMA # (Z-A)' },
];

function refName(value) {
  if (!value) return '—';
  if (typeof value === 'string') return value;
  return value.Name || '—';
}

export default function MyRmas() {
  const { branding } = useBranding();
  const list = useMarketplaceList({ fetcher: getRmas, requiredConnectRole: CONNECT_ROLES.RMA });
  const {
    rows, totalCount, totalPages, loading, error,
    isGuest, isPending, hasAccess, roleReady,
    searchInput, setSearchInput, sortBy, setSortBy, page, setPage, refetch,
  } = list;

  const [expandedId, setExpandedId] = useState(null);

  if (isGuest) return null;

  const blocked = isPending || (roleReady && !hasAccess) || isInsufficientPermissionError(error);
  const hasActiveFilter = !!searchInput;
  const isTrulyEmpty = !loading && totalCount === 0 && !hasActiveFilter;
  const hasNoMatches = !loading && totalCount === 0 && hasActiveFilter;

  const toggle = (id) => setExpandedId((cur) => (cur === id ? null : id));

  return (
    <div style={s.page}>
      <AppHeader title="RMAs" />
      <main style={s.main}>
        <div style={s.container}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button type="button" style={s.refreshBtn} onClick={refetch} disabled={loading} title="Refresh from Salesforce">
              {loading ? '…' : '↻ Refresh'}
            </button>
          </div>

          <div style={s.filterRow}>
            <div style={s.searchWrap}>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by RMA #, type, or status…"
                style={s.searchInput}
              />
            </div>
            <select value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(1); }} style={s.sortSelect}>
              {SORT_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>

          {loading && <p style={s.muted}>Loading RMAs&hellip;</p>}

          <PermissionGate blocked={blocked} primaryColor={branding.primaryColor} minHeight={320}>
            {isTrulyEmpty && (
              <div style={s.emptyCard}><p style={s.muted}>No RMAs found for your account.</p></div>
            )}
            {hasNoMatches && (
              <div style={s.emptyCard}><p style={s.muted}>No RMAs match your search.</p></div>
            )}

            {!loading && rows.map((r) => {
              const st = statusStyle(r.inscor__Status__c);
              const isExpanded = expandedId === r.Id;
              const lines = Array.isArray(r.lines) ? r.lines : [];
              return (
                <div key={r.Id} style={s.card}>
                  <div
                    style={s.cardHeader}
                    onClick={() => toggle(r.Id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(r.Id); } }}
                    aria-expanded={isExpanded}
                  >
                    <div style={s.cardLeft}>
                      <span style={s.cardName}>{r.Name}</span>
                      <span style={{ ...s.statusBadge, color: st.color, backgroundColor: st.bg }}>{st.label}</span>
                    </div>
                    <div style={s.cardRight}>
                      <span style={s.linesHint}>{isExpanded ? 'Hide lines' : `View lines (${lines.length})`}</span>
                      <span style={{ ...s.expandChevron, transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                        <svg viewBox="0 0 24 24" fill="none" width={18} height={18}
                          stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="m6 9 6 6 6-6" />
                        </svg>
                      </span>
                    </div>
                  </div>
                  <div style={s.detailRow}>
                    <div style={s.detailItem}>
                      <span style={s.detailLabel}>Type</span>
                      <span style={s.detailValue}>{r.inscor__Type__c || '—'}</span>
                    </div>
                    <div style={s.detailItem}>
                      <span style={s.detailLabel}>Create Date</span>
                      <span style={s.detailValue}>{formatDate(r.inscor__Create_Date__c)}</span>
                    </div>
                    <div style={s.detailItem}>
                      <span style={s.detailLabel}>Status</span>
                      <span style={s.detailValue}>{st.label}</span>
                    </div>
                    <div style={s.detailItem}>
                      <span style={s.detailLabel}>Customer PO Number</span>
                      <span style={s.detailValue}>{r.inscor__Customer_PO_Number__c || '—'}</span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div style={s.cardBody}>
                      {lines.length === 0 && <p style={s.muted}>No lines found.</p>}
                      {lines.length > 0 && (
                        <div style={s.lineTableWrap}>
                          <table style={s.lineTable}>
                            <thead>
                              <tr>
                                <th style={s.lineTh}>RMA Line ID</th>
                                <th style={s.lineTh}>Part Number</th>
                                <th style={s.lineTh}>Release Line</th>
                                <th style={s.lineTh}>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {lines.map((l, idx) => {
                                const lst = statusStyle(l.inscor__Status__c);
                                return (
                                  <tr key={l.Id || idx} style={idx % 2 === 0 ? s.lineRowEven : undefined}>
                                    <td style={s.lineTd}>{l.Name || '—'}</td>
                                    <td style={s.lineTd}>{l.inscor__Part_Number__c || '—'}</td>
                                    <td style={s.lineTd}>{refName(l.inscor__Release_Line__c)}</td>
                                    <td style={s.lineTd}>
                                      <span style={{ ...s.statusBadge, color: lst.color, backgroundColor: lst.bg }}>{lst.label}</span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </PermissionGate>

          {!loading && totalCount > 0 && (
            <div style={s.pager}>
              <button type="button" style={page <= 1 ? s.pagerBtnDisabled : s.pagerBtn} disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
              <span style={s.pagerInfo}>Page {page} of {totalPages} ({totalCount} RMA{totalCount === 1 ? '' : 's'})</span>
              <button type="button" style={page >= totalPages ? s.pagerBtnDisabled : s.pagerBtn} disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
            </div>
          )}

          {error && !isInsufficientPermissionError(error) && (
            <p style={s.err}>{error.message || 'Failed to load RMAs'}</p>
          )}
        </div>
      </main>
    </div>
  );
}
